import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  variant?: 'default' | 'destructive';
  extraInfo?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = '确认',
  cancelText = '取消',
  onConfirm,
  variant = 'default',
  extraInfo,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {extraInfo && <div className="py-2">{extraInfo}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cancelText}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            className={
              variant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg hover:shadow-xl font-semibold transition-all'
                : 'bg-accent text-accent-foreground hover:bg-accent-hover shadow-lg hover:shadow-xl font-semibold transition-all'
            }
            style={
              variant === 'destructive'
                ? {
                    background:
                      'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: '#ffffff',
                    boxShadow:
                      '0 4px 12px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  }
                : {
                    background:
                      'linear-gradient(135deg, #ff5c5c 0%, #ff7070 100%)',
                    color: '#ffffff',
                    boxShadow:
                      '0 4px 12px rgba(255, 92, 92, 0.4), 0 0 0 1px rgba(255, 92, 92, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  }
            }
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// 提供静态方法，类似 antd Modal.confirm()
export const confirm = ({
  title,
  content,
  okText = '确认',
  cancelText = '取消',
  onOk,
  onCancel,
  type = 'confirm',
}: {
  title: string;
  content?: React.ReactNode;
  okText?: string;
  cancelText?: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  type?: 'confirm' | 'warning' | 'error' | 'info';
}) => {
  const iconColor =
    type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#ff5c5c';

  return Modal.confirm({
    title,
    content,
    okText,
    cancelText,
    onOk,
    onCancel,
    icon: <ExclamationCircleFilled style={{ color: iconColor }} />,
    okButtonProps: {
      danger: type === 'error',
      style:
        type === 'error'
          ? {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            }
          : {
              background: 'linear-gradient(135deg, #ff5c5c 0%, #ff7070 100%)',
            },
    },
    centered: true,
    styles: {
      container: {
        backgroundColor: 'var(--card)',
        border: '1px solid var(--border)',
      },
      header: {
        backgroundColor: 'transparent',
      },
      body: {
        color: 'var(--text)',
      },
    },
  });
};
